import { View } from "react-native";
import React, { useEffect, useState } from "react";
import MainContainer from "components/MainContainer";
import { AccountStackParamList } from "navigation/types";
import BaseCard, { BaseCardListItem } from "components/BaseCard";
import {
  AccountDetails,
  AccountMemberDetails,
  SubscriptionDetails,
} from "features/types";
import { Text } from "@ui-kitten/components";
import AddNewMemberCard from "./AddNewMemberCard";
import AutoRenewalCard from "./AutoRenewalCard";
import PlanNameWithPaymentInterval from "screens/Account/components/PlanNameWithPaymentInterval";
import Spacer from "components/Spacer";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useAccount } from "hooks/useAccount";
import i18n from "plugins/localization";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import { memberId } from "utils/userUtils";

export interface MemberTreeItem {
  name: string;
  id: number;
  subscriptions: SubscriptionDetails[];
  type: AccountMemberDetails["type"];
}

const createMemberTree = (account: AccountDetails) =>
  account.members.map((member) => {
    const {
      id: memberId,
      user: { firstName, lastName },
      type,
    } = member;
    const { subscriptions } = account;

    const result = {
      name: `${firstName} ${lastName}`,
      id: memberId,
      subscriptions: subscriptions.filter(
        (sub) =>
          filterIneligibleSubscriptions(sub) &&
          sub.child === false &&
          sub.member &&
          sub.member.id === memberId
      ),
      type,
    } as MemberTreeItem;

    return result;
  });

const RenderUserInfo: React.FC<{ member: MemberTreeItem }> = ({ member }) => {
  return (
    <View>
      {member.type === "PRIMARY" && (
        <Text category="c1">{i18n.t("common.accountOwner")}</Text>
      )}
      <Text category="h6">{member.name}</Text>
      <Text>
        {i18n.t("common.memberId", {
          memberId: memberId(member.id),
        })}
      </Text>
      {member.subscriptions.map((sub) => (
        <PlanNameWithPaymentInterval
          key={sub.id}
          planPackage={sub.planPackage}
          billingInterval={sub.billingInterval}
          monthlyPrice={sub.monthlyPrice}
        />
      ))}
    </View>
  );
};

const ManageSubscriptionsScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<AccountStackParamList>>();
  const { account } = useAccount();

  const [memberTree, setMemberTree] = useState<MemberTreeItem[]>([]);

  useEffect(() => {
    if (!account) return;

    const accountMemberTree = createMemberTree(account);
    accountMemberTree.sort((a) => (a.type === "PRIMARY" ? -1 : 1)); // always place account owner on top
    setMemberTree(accountMemberTree);
  }, [account]);

  return (
    <MainContainer
      grayBackground
      goBackAction={() => navigation.navigate("AccountScreen")}
    >
      <View style={{ padding: 15 }}>
        <BaseCard title={i18n.t("screens.ManageSubscriptions.managePlans")}>
          {memberTree.map((member) => (
            <BaseCardListItem
              testID="member-plan-detail-item"
              key={member.id}
              title={<RenderUserInfo member={member}></RenderUserInfo>}
              onPress={() =>
                navigation.replace("ManageSubscriptions", {
                  screen: "SubscriptionDetail",
                  params: { memberId: String(member.id) },
                })
              }
            ></BaseCardListItem>
          ))}
        </BaseCard>

        <Spacer />

        <AutoRenewalCard />

        <Spacer />

        {account?.type === "FULL" && <AddNewMemberCard />}

        <Spacer />

        {/* <BaseCard
          title="Gift a Membership"
          titleProps={{ style: { color: "white" } }}
          style={{ backgroundColor: "rgb(110, 110, 110)" }}
        >
          <Divider style={{ marginVertical: 15 }} />
          <Pressable activeOpacity={ACTIVE_OPACITY}>
            <Text style={{ textAlign: "center", color: "white" }}>
              Give the gift of a membership to someone special and attend events
              together
            </Text>
            <View style={{ alignItems: "center", marginVertical: 20 }}>
              <Ionicons name="gift-outline" size={120} color="white" />
            </View>
          </Pressable>
        </BaseCard> */}
      </View>
    </MainContainer>
  );
};

export default ManageSubscriptionsScreen;
